/**
 * ============================================================================
 * [P5] App.tsx - 탭 컴포넌트 통합 완료
 *
 * 메인 애플리케이션 컴포넌트
 * - MainLayout으로 전체 레이아웃 구성
 * - 인증 상태에 따른 콘텐츠 표시
 * - 모든 탭 컴포넌트 연결 완료
 * ============================================================================
 */

import { MainLayout } from '@/components/layout'
import { DualEditor } from '@/components/editor'
import { SavedTab } from '@/components/saved'
import { TrackingTab } from '@/components/tracking'
import { ManagementTab } from '@/components/management'
import { URLLinkTab } from '@/components/urllink'
import { BackupTab } from '@/components/backup'
import { useAppStore, useCurrentTab } from '@/stores'
import './App.css'

// ============================================================================
// [P1-02] 메인 콘텐츠 컴포넌트
// - 탭에 따른 콘텐츠 렌더링
// ============================================================================
function MainContent() {
  const currentTab = useCurrentTab()

  return (
    <div className="main-content">
      {/* ================================================================
          탭 네비게이션 (임시)
          - 추후 TabNavigation 컴포넌트로 분리
          ================================================================ */}
      <TabNavigation />

      {/* ================================================================
          탭 콘텐츠
          ================================================================ */}
      <div className="tab-content-container">
        {currentTab === 'writing' && <WritingTabContent />}
        {currentTab === 'saved' && <SavedTab />}
        {currentTab === 'tracking' && <TrackingTab />}
        {currentTab === 'management' && <ManagementTab />}
        {currentTab === 'urllink' && <URLLinkTab />}
        {currentTab === 'backup' && <BackupTab />}
      </div>
    </div>
  )
}

// ============================================================================
// 탭 네비게이션 컴포넌트 (임시)
// ============================================================================
function TabNavigation() {
  const currentTab = useCurrentTab()
  const setCurrentTab = useAppStore((state) => state.setCurrentTab)

  const tabs = [
    { id: 'writing', icon: '✏️', text: '글 작성' },
    { id: 'saved', icon: '💾', text: '저장된 글' },
    { id: 'tracking', icon: '📊', text: '트래킹' },
    { id: 'management', icon: '📋', text: '스크립트 작성' },
    { id: 'urllink', icon: '🔗', text: 'URL 연결' },
    { id: 'backup', icon: '💾', text: '백업' },
  ] as const

  return (
    <div className="tab-navigation">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={`tab-button ${currentTab === tab.id ? 'active' : ''}`}
          onClick={() => setCurrentTab(tab.id)}
          role="tab"
          aria-selected={currentTab === tab.id}
        >
          <span className="tab-icon">{tab.icon}</span>
          <span className="tab-text">{tab.text}</span>
        </button>
      ))}
    </div>
  )
}

// ============================================================================
// 탭 콘텐츠 컴포넌트들 (임시 플레이스홀더)
// - 추후 각 탭별 컴포넌트로 분리
// ============================================================================
function WritingTabContent() {
  return (
    <div className="tab-content">
      {/* ================================================================
          P1-03: DualEditor 컴포넌트 연동
          - 레퍼런스 글 + 수정/작성 글 듀얼 에디터
          ================================================================ */}
      <DualEditor />
    </div>
  )
}

// ============================================================================
// [P5] 탭 컴포넌트 연결 완료
// - SavedTab, TrackingTab, ManagementTab, URLLinkTab, BackupTab
// - 각 탭 컴포넌트는 @/components/{folder} 에서 import
// ============================================================================

// ============================================================================
// [P1-02] 메인 App 컴포넌트
// ============================================================================
function App() {
  return (
    <MainLayout showFooter>
      <MainContent />
    </MainLayout>
  )
}

export default App
